// apps/web/src/components/ThemeToggle.tsx

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Brightness4 from '@mui/icons-material/Brightness4';
import Brightness7 from '@mui/icons-material/Brightness7';
import { useTheme } from '../contexts/ThemeContext';

interface ThemeToggleProps {
  /**
   * Size of the icon button
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Tooltip text for light mode (shows when hovering in light mode)
   * @default 'Dark Mode'
   */
  lightTooltip?: string;

  /**
   * Tooltip text for dark mode (shows when hovering in dark mode)
   * @default 'Light Mode'
   */
  darkTooltip?: string;

  /**
   * Optional className for custom styling
   */
  className?: string;
}

/**
 * Theme toggle button component
 *
 * Displays a moon icon in light mode and a sun icon in dark mode.
 * Clicking the button toggles between light and dark themes.
 *
 * Features:
 * - Animated rotation on hover
 * - Accessible tooltips
 * - ARIA labels for screen readers
 * - Smooth theme transitions
 *
 * Usage:
 * <ThemeToggle />
 * <ThemeToggle size="small" lightTooltip="Enable Dark Mode" />
 */
export function ThemeToggle({
  size = 'medium',
  lightTooltip = 'Dark Mode',
  darkTooltip = 'Light Mode',
  className,
}: ThemeToggleProps) {
  const { mode, toggleTheme } = useTheme();

  const tooltipText = mode === 'light' ? lightTooltip : darkTooltip;
  const ariaLabel = mode === 'light' ? lightTooltip : darkTooltip;

  return (
    <Tooltip title={tooltipText}>
      <IconButton
        onClick={toggleTheme}
        size={size}
        className={className}
        aria-label={ariaLabel}
        sx={{
          transition: 'transform 0.3s ease, background-color 0.2s ease',
          '&:hover': {
            transform: 'rotate(20deg)',
          },
        }}
      >
        {mode === 'light' ? <Brightness4 /> : <Brightness7 />}
      </IconButton>
    </Tooltip>
  );
}
